// Cloud web entry — mounts the shared @onestone/app tree under BrowserRouter
// (clean-path routing on app.onestoneai.com). The desktop host uses HashRouter
// instead (Electron's file:// protocol). Both surfaces render the same
// <AppProviders>/<AppRoutes> from @onestone/app — the Router choice is the only
// host difference. No Electron preload here, so the app's window.electronAPI
// call sites are graceful no-ops (isElectron() === false) and api.ts's
// resolveBaseUrl() returns '' → same-origin /api/* on the Worker.
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import '@fontsource-variable/geist/index.css';
import '@fontsource-variable/geist-mono/index.css';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { AppProviders, AppRoutes } from '@/AppRoot';
import { registerServiceWorker } from '@/lib/pwa';
import { listenForWindowErrors, rootErrorOptions } from '@/core/rendererErrorReporter';
import { CloudAuthGate } from './auth/CloudAuthGate';
import { LinkDevice } from './link/LinkDevice';
// Shared design tokens + Tailwind entry (OneStone color tokens, custom variants,
// and the @source globs that keep @onestone/ui + @onestone/app + apps/cloud-web
// utilities in the build). Lives in @onestone/app — the shared app package both
// hosts consume — so the web app no longer reaches into the desktop renderer.
import '@/styles/global.css';

const rootElement = document.getElementById('app');
if (!rootElement) {
  throw new Error('Root element #app not found — check apps/cloud-web/index.html');
}

// Offline shell + user-gated update flow for the installed cloud PWA
// (no-op outside secure contexts; Electron never runs this bundle).
registerServiceWorker();

// Same error reporting as the desktop host (core/rendererErrorReporter.ts).
// The cloud has no diagnostics route yet (MA-443), so reports are dropped there
// until it does; the reporter is fire-and-forget either way.
listenForWindowErrors();
createRoot(rootElement, rootErrorOptions).render(
  <StrictMode>
    <CloudAuthGate>
      <AppProviders>
        <BrowserRouter>
          {/* /link is a cloud-only, standalone RFC 8252 loopback link screen —
              a static route so it outranks the /* splat that mounts the shared
              app shell. CloudAuthGate above already requires a session, so the
              one-time-token is always minted for the signed-in user. */}
          <Routes>
            <Route path="/link" element={<LinkDevice />} />
            <Route path="/*" element={<AppRoutes />} />
          </Routes>
        </BrowserRouter>
      </AppProviders>
    </CloudAuthGate>
  </StrictMode>,
);
